import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogIn, Menu, Phone, ShieldCheck, UserCircle2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import LanguageToggle from '@/components/LanguageToggle';
import { useLang } from '@/i18n/LangContext';
import { SITE_NAV } from '@/components/site/siteNav';

const tx = (v, lang) => (v == null ? '' : typeof v === 'string' ? v : (v[lang] || v.en || v.hi || ''));

/**
 * Shared public-website header: logo, mega-menu navigation, language toggle,
 * Get Free Quote CTA and Login menu. Used by Landing (home) and SiteLayout.
 */
export function SiteHeader({ onQuote, phone }) {
    const nav = useNavigate();
    const loc = useLocation();
    const { t, lang } = useLang();
    const [open, setOpen] = useState(null);      // desktop mega-menu key
    const [mobOpen, setMobOpen] = useState(false);
    const closeTimer = useRef(null);

    useEffect(() => { setOpen(null); setMobOpen(false); }, [loc.pathname]);

    const enter = (key) => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(key); };
    const leave = () => { closeTimer.current = setTimeout(() => setOpen(null), 120); };

    return (
        <header className='sticky top-0 z-40 bg-card/95 backdrop-blur border-b' data-testid='site-header'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3'>
                <Link to='/' className='flex items-center shrink-0 h-14' data-testid='site-logo-link'>
                    <img src='/uppower-logo.png' alt='UPPOWER Solar' className='h-12 sm:h-14 w-auto object-contain' />
                </Link>

                {/* Desktop nav */}
                <nav className='hidden lg:flex items-center gap-0.5' aria-label='Main'>
                    <NavLink to='/' end className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-secondary ${isActive ? 'text-primary' : 'text-foreground/80 hover:text-primary'}`} data-testid='site-nav-home'>{t('nav_home')}</NavLink>
                    {SITE_NAV.map(item => item.columns ? (
                        <div key={item.key} className='relative' onMouseEnter={() => enter(item.key)} onMouseLeave={leave}>
                            <button
                                type='button'
                                className={`px-3 py-2 text-sm font-medium rounded-md inline-flex items-center gap-1 hover:bg-secondary ${open === item.key ? 'text-primary bg-secondary' : 'text-foreground/80 hover:text-primary'}`}
                                aria-expanded={open === item.key}
                                onClick={() => setOpen(open === item.key ? null : item.key)}
                                data-testid={`site-nav-${item.key}`}
                            >
                                {tx(item.label, lang)} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-150 ${open === item.key ? 'rotate-180' : ''}`} />
                            </button>
                            {open === item.key && (
                                <div className='absolute left-0 top-full pt-2 z-50' data-testid={`site-megamenu-${item.key}`}>
                                    <div className='rounded-2xl border bg-card/95 backdrop-blur-xl shadow-xl p-5 grid gap-6 min-w-[560px]' style={{ gridTemplateColumns: `repeat(${item.columns.length}, minmax(0, 1fr))` }}>
                                        {item.columns.map((col, ci) => (
                                            <div key={ci}>
                                                <div className='text-[11px] font-bold uppercase tracking-widest text-accent mb-2'>{tx(col.title, lang)}</div>
                                                <ul className='space-y-0.5'>
                                                    {col.items.map(li => (
                                                        <li key={li.to}>
                                                            <Link to={li.to} className='block rounded-lg px-2.5 py-2 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent' data-testid={`site-menu-link-${li.to.replace(/\//g, '-').replace(/^-/, '')}`}>
                                                                <div className='text-sm font-semibold'>{tx(li.label, lang)}</div>
                                                                {li.desc && <div className='text-xs text-muted-foreground'>{tx(li.desc, lang)}</div>}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <NavLink key={item.key} to={item.to} className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-secondary ${isActive ? 'text-primary' : 'text-foreground/80 hover:text-primary'}`} data-testid={`site-nav-${item.key}`}>{tx(item.label, lang)}</NavLink>
                    ))}
                </nav>

                <div className='flex items-center gap-2 shrink-0'>
                    {phone && (
                        <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className='hidden xl:inline-flex items-center gap-1.5 text-sm font-semibold text-primary px-2' data-testid='site-header-phone'>
                            <Phone className='w-4 h-4' /> {phone}
                        </a>
                    )}
                    <LanguageToggle />
                    <Button className='hidden md:inline-flex bg-accent hover:bg-accent/90 text-accent-foreground' onClick={() => onQuote?.()} data-testid='landing-get-quote-nav'>
                        {t('get_free_quote')}
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant='outline' size='sm' data-testid='landing-login-menu'>
                                <LogIn className='w-4 h-4 mr-1' /> Login
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end' className='w-56'>
                            <DropdownMenuLabel>{t('login') || 'Login as'}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => nav('/customer/login')} data-testid='landing-login-customer'>
                                <UserCircle2 className='w-4 h-4 mr-2' />
                                <div className='flex flex-col'><span className='font-medium'>Customer Login</span><span className='text-xs text-muted-foreground'>ग्राहक लॉगिन</span></div>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => nav('/admin/login')} data-testid='landing-login-admin'>
                                <ShieldCheck className='w-4 h-4 mr-2' />
                                <div className='flex flex-col'><span className='font-medium'>Admin / Staff Login</span><span className='text-xs text-muted-foreground'>एडमिन / स्टाफ</span></div>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => nav('/customer/signup')} data-testid='landing-login-signup'>
                                <UserPlus className='w-4 h-4 mr-2' />
                                <div className='flex flex-col'><span className='font-medium'>New Here? Sign up</span><span className='text-xs text-muted-foreground'>नया खाता बनाएं</span></div>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <button type='button' className='lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md hover:bg-secondary' onClick={() => setMobOpen(true)} aria-label='Menu' data-testid='site-mobile-menu-btn'><Menu className='w-5 h-5' /></button>
                </div>
            </div>

            {/* Mobile drawer */}
            <Sheet open={mobOpen} onOpenChange={setMobOpen}>
                <SheetContent side='right' className='w-[88vw] max-w-sm p-0 flex flex-col' data-testid='site-mobile-menu'>
                    <SheetHeader className='px-4 py-3 border-b text-left'>
                        <SheetTitle className='text-base'>Menu</SheetTitle>
                    </SheetHeader>
                    <div className='flex-1 overflow-y-auto px-2 py-2'>
                        <Link to='/' className='block px-3 py-3 rounded-md hover:bg-secondary text-sm font-semibold' data-testid='site-mobile-nav-home'>{t('nav_home')}</Link>
                        <Accordion type='multiple' className='w-full'>
                            {SITE_NAV.map(item => item.columns ? (
                                <AccordionItem key={item.key} value={item.key} className='border-b-0'>
                                    <AccordionTrigger className='px-3 py-3 text-sm font-semibold hover:no-underline hover:bg-secondary rounded-md' data-testid={`site-mobile-nav-${item.key}`}>{tx(item.label, lang)}</AccordionTrigger>
                                    <AccordionContent className='pb-1'>
                                        {item.columns.map((col, ci) => (
                                            <div key={ci} className='mb-2'>
                                                <div className='px-5 pt-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-accent'>{tx(col.title, lang)}</div>
                                                {col.items.map(li => (
                                                    <Link key={li.to} to={li.to} className='block px-5 py-2.5 text-sm rounded-md hover:bg-secondary min-h-[44px] flex items-center' data-testid={`site-mobile-link-${li.to.replace(/\//g, '-').replace(/^-/, '')}`}>{tx(li.label, lang)}</Link>
                                                ))}
                                            </div>
                                        ))}
                                    </AccordionContent>
                                </AccordionItem>
                            ) : (
                                <Link key={item.key} to={item.to} className='block px-3 py-3 rounded-md hover:bg-secondary text-sm font-semibold' data-testid={`site-mobile-nav-${item.key}`}>{tx(item.label, lang)}</Link>
                            ))}
                        </Accordion>
                        <div className='border-t mt-2 pt-2 space-y-1 px-1'>
                            <Button variant='outline' className='w-full justify-start min-h-[44px]' onClick={() => { setMobOpen(false); nav('/customer/login'); }} data-testid='mob-login-customer'><UserCircle2 className='w-4 h-4 mr-2' /> Customer Login / ग्राहक</Button>
                            <Button variant='outline' className='w-full justify-start min-h-[44px]' onClick={() => { setMobOpen(false); nav('/admin/login'); }} data-testid='mob-login-admin'><ShieldCheck className='w-4 h-4 mr-2' /> Admin / Staff Login</Button>
                            <Button variant='ghost' className='w-full justify-start min-h-[44px]' onClick={() => { setMobOpen(false); nav('/customer/signup'); }} data-testid='mob-login-signup'><UserPlus className='w-4 h-4 mr-2' /> New account / नया खाता</Button>
                        </div>
                    </div>
                    <div className='p-3 border-t'>
                        <Button className='w-full min-h-[48px] bg-accent hover:bg-accent/90 text-accent-foreground' onClick={() => { setMobOpen(false); onQuote?.(); }} data-testid='site-mobile-quote-btn'>{t('get_free_quote')}</Button>
                    </div>
                </SheetContent>
            </Sheet>
        </header>
    );
}

export default SiteHeader;
